import { ActionBar } from '@/modules/application';
import { Card, CardContent, Typography, Button } from '@mui/material';
import { FunctionComponent } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UserFormModel } from './UserFormModel';
import { ActionBarDeleteButton } from '@/modules/application/components/ActionBarDeleteButton';
import { userService } from '@/modules/users/api/users/users.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useUser } from '@/modules/users/contexts/UserContext';
import { useTranslation } from '@/lib';
import AssignedLocationsTable from './AssignedLocationsTable';
import { UserModel } from '../../../types/UserModel';
import { useLocationManagement } from './hooks';
import { UserFormFields, GuestPermissionsCard } from './components';

interface UserFormDisplayProps {
  form: UseFormReturn<UserFormModel>;
  onSubmit: (data: UserFormModel) => void;
  isCreate?: boolean;
  userId?: number;
  userData?: UserModel;
}

const UserFormDisplay: FunctionComponent<UserFormDisplayProps> = ({ form, onSubmit, isCreate = false, userId, userData }) => {
  const { t } = useTranslation();
  const { formState, handleSubmit, control } = form;
  const { errors, isSubmitting } = formState;
  const navigate = useNavigate();
  const hasUserEditPermission = useUser().hasPermission(Permission.USER_EDIT);

  // Use custom hook for location management
  const { userLocations, handleRemoveLocation, handleAddLocation, isGuestUser, isAdminUser } = useLocationManagement({
    form,
    userId,
    userData,
  });

  const deleteUser = async () => {
    if (userId) {
      const response = await userService.deleteUser(userId);
      if (!response.isSuccess) {
        toast.error(t('error_deleting_user'));
      } else {
        toast.success(t('user_deleted'));
        navigate('/app/configuration/users');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3 ">
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('details')}
            </Typography>
            <UserFormFields control={control} errors={errors} isGuestUser={isGuestUser} />
          </CardContent>
        </Card>

        {/* Guest Permissions Card - Only show for GUEST users */}
        {isGuestUser && <GuestPermissionsCard control={control} />}

        {/* Assigned Locations Table - guests and operators can be location-scoped, admins never are */}
        {!isAdminUser && (
          <AssignedLocationsTable userLocations={userLocations} onRemoveLocation={handleRemoveLocation} onAddLocation={handleAddLocation} />
        )}
      </div>

      <ActionBar visible={hasUserEditPermission}>
        <Button variant="contained" type="submit" loading={isSubmitting}>
          {t('save')}
        </Button>
        <ActionBarDeleteButton
          deleteConfirmationQuestion={t('confirm_delete_user')}
          onDeleteConfirmed={deleteUser}
          isVisible={!isCreate}
          isLoading={isSubmitting}
        />
      </ActionBar>
    </form>
  );
};

export default UserFormDisplay;
